import _eol from "eol";
import _flexbox from "../dict/flexbox.js";
import _convertToHtml from "../functions/quill/convertToHtml.js";
import _removeInvalidEvents from "../functions/eventGroups/removeInvalidEvents.js";
import _tcLib from "../lib/timecode.js";
import _Event from "../classes/event.js";
const Event = _Event;
const tcLib = _tcLib;
const removeInvalidEvents = _removeInvalidEvents;
const convertToHtml = _convertToHtml;
const flexbox = _flexbox;
const eol = _eol;

/**
 * Parse timecode in format HH:MM:SS.FF (period-separated frames)
 * @param {string} tc - Timecode string
 * @param {number} frameRate - Frame rate
 * @param {boolean} dropFrame - Drop frame flag
 * @returns {number} - Time in seconds
 */
function parseCipTimecode(tc, frameRate, dropFrame) {
  // Convert period separator to colon for standard SMPTE parsing
  // Format: HH:MM:SS.FF -> HH:MM:SS:FF
  const normalizedTc = tc.replace(/\.(\d{2})$/, ":$1");
  return tcLib.tcToSec(normalizedTc, frameRate, dropFrame);
}
export default {
  decode: async function (input, options) {
    let events = [];
    // Frame rate is determined by getFrameRateInfoFromFile.js and passed via options
    const frameRate = options.frameRate || 25;
    const dropFrame = options.dropFrame || false;
    const matchRegex = /\\[a-z]+\d*|[{}]|[^\\{}]+/gi;
    let cleanLines = [];
    let fileInfoEndFlag = false;
    /* Parse RTF to JSON */
    // console.log(input);
    // let fileLines = eol.split(input);

    //Remove line breaks from input and split on \par or \pard command instead
    // console.log(input);
    let fileLines = eol.split(input).join("").split(/\\pard|\\par/gmi);
    fileLines.forEach((line, index) => {
      if (!fileInfoEndFlag || !line) {
        if (line.includes("FILE_INFO_END")) {
          fileInfoEndFlag = true;
        }
        //Skip to next line
        return;
      }
      if (line.includes("{\\*\\")) {
        //Skip metadata lines
        fileInfoEndFlag = false;
        return;
      }

      // console.log("--------------------------");
      // console.log(`Line ${index + 1}:`);
      // console.log(line);

      //replace tabs with spaces
      line = line.replace(/\t|\\tab/g, "-->");

      // First, convert RTF unicode escape sequences to actual characters
      // Pattern: \u#### where #### is a unicode code point (can be negative)

      line = line.replace(/\\u(-?\d+)/g, (match, code) => {
        // console.log(code);
        const codePoint = parseInt(code, 10);
        // Handle negative values (RTF uses signed 16-bit)
        const unsigned = codePoint < 0 ? codePoint + 65536 : codePoint;
        return String.fromCharCode(unsigned);
      }).replace(/\\uc\d*/g, ''); // Remove \ucN control words

      // console.log("Processed Line:");
      // console.log(line);

      //We want to split on the backslash character or backslash followed by a space characters.
      let cmds = line.match(matchRegex).filter(cmd => cmd.length > 0 && cmd !== "}" && cmd !== "{");
      let text = "";
      // console.log(cmds);
      cmds.forEach((cmd, i, allCmds) => {
        if (cmd.startsWith("\\")) {
          //This is a command
          // console.log(`  Command: ${cmd}`);
          if (cmd === "\\i") {
            text += "<i>";
          } else if (cmd === "\\i0") {
            text += "</i>";
          } else if (cmd === "\\b") {
            text += "<b>";
          } else if (cmd === "\\b0") {
            text += "</b>";
          } else if (cmd === "\\ul") {
            text += "<u>";
          } else if (cmd === "\\ul0") {
            text += "</u>";
          }
          if (allCmds[i + 1]) {
            //Trim whitespace from the start of the next command
            allCmds[i + 1] = allCmds[i + 1].replace(/^\s+/, '');
          }
        } else {
          //This is text
          text += cmd;
        }
      });
      if (text.length > 0) {
        //replace nul and other control codes from text before adding it to the clean lines
        cleanLines.push(text.replace(/-->/g, '\t').replace(/\0/g, ''));
      }
    });

    // console.log(JSON.stringify(cleanLines, null, 2));

    // Parse cleaned lines to extract subtitle events
    // Pattern for timecode header: {#00001 00:00:02.00 00:00:05.16 ...}
    const timecodeHeaderRegex = /#\d+\s+\d{2}:\d{2}:\d{2}\.\d{2}/;
    let i = 0;
    let currentSubtitle = null;
    let textLines = [];
    while (i < cleanLines.length) {
      let line = cleanLines[i];

      // Skip empty lines
      if (!line || line.trim().length === 0) {
        i++;
        continue;
      }

      // Strip curly braces, format tags (b, u, and i), and whitespace for checking
      const cleanLine = line.replace(/[{}]/g, '').replace(/<b>|<\/b>|<u>|<\/u>|<i>|<\/i>/g, '').trim();
      // console.log(cleanLine);

      // Check if this line is a timecode header
      if (timecodeHeaderRegex.test(cleanLine)) {
        // Save previous subtitle if exists
        if (currentSubtitle && textLines.length > 0) {
          const ccEvent = new Event();
          ccEvent.start = currentSubtitle.startSec;
          ccEvent.end = currentSubtitle.endSec;
          ccEvent.text = convertToHtml(textLines.join('\n'));
          ccEvent.alignment = "center";
          events.push(ccEvent);
        }

        // Parse new timecode header
        try {
          // Split on tabs/spaces and extract timecodes
          const parts = cleanLine.split(/\s+/).filter(p => p.trim().length > 0);
          while (parts.length > 0 && !parts[0].startsWith("#")) {
            parts.shift();
          }

          // console.log(parts);
          // parts[0] = #00001
          // parts[1] = start timecode (HH:MM:SS.FF)
          // parts[2] = end timecode (HH:MM:SS.FF)

          if (parts.length >= 3) {
            const startTc = parts[1];
            const endTc = parts[2];

            // Validate timecode format
            const timecodeFormat = /^\d{2}:\d{2}:\d{2}\.\d{2}$/;
            if (timecodeFormat.test(startTc) && timecodeFormat.test(endTc)) {
              currentSubtitle = {
                startSec: parseCipTimecode(startTc, frameRate, dropFrame),
                endSec: parseCipTimecode(endTc, frameRate, dropFrame)
              };
              textLines = [];
            } else {
              console.warn(`Invalid timecode format in line ${i + 1}: ${line}`);
              currentSubtitle = null;
            }
          } else {
            console.warn(`Malformed timecode header in line ${i + 1}: ${line}`);
            currentSubtitle = null;
          }
        } catch (error) {
          console.warn(`Error parsing timecode header in line ${i + 1}: ${error.message}`);
          currentSubtitle = null;
        }
      } else {
        // This is a text line - add to current subtitle (already cleaned)
        if (currentSubtitle && cleanLine.length > 0) {
          textLines.push(cleanLine);
        }
      }
      i++;
    }

    // Don't forget the last subtitle
    if (currentSubtitle && textLines.length > 0) {
      const ccEvent = new Event();
      ccEvent.start = currentSubtitle.startSec;
      ccEvent.end = currentSubtitle.endSec;
      ccEvent.text = convertToHtml(textLines.join('\n'));
      ccEvent.alignment = "center";
      events.push(ccEvent);
    }
    return events;
  },
  encode: function (eventGroup, options) {
    throw new Error("Cavena CIP files are not supported for encoding by Closed Caption Converter.");
  },
  preProcess: {
    encode: function (eventGroup) {
      return removeInvalidEvents(eventGroup);
    },
    decode: function (input) {
      return input;
    }
  },
  postProcess: {
    encode: function (output) {
      return output;
    },
    decode: function (eventGroup) {
      return eventGroup;
    }
  }
};